import { DogStage } from '../core/types';

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const DAYS_THRESHOLD = 14;
const ADULT_GRACE_PERIOD_MONTHS = 2;

/**
 * Determines the effective dog stage, forcing 'adult' when the pet
 * is within 14 days of their adult_when date or has already passed it.
 */
export function getEffectiveDogStage(
  pet: { dog_stage?: string; adult_when?: string } | null | undefined,
  dogStageOverride?: DogStage,
): DogStage {
  if (dogStageOverride) return dogStageOverride;

  const currentStage = (pet?.dog_stage as DogStage) || 'adult';
  if (currentStage !== 'puppy' || !pet?.adult_when) return currentStage;

  const daysUntilAdult = (new Date(pet.adult_when).getTime() - Date.now()) / MS_PER_DAY;
  return daysUntilAdult <= DAYS_THRESHOLD ? 'adult' : currentStage;
}

/**
 * Returns true once the pet has been an "adult" (by `adult_when`) for longer
 * than the grace period (2 months) while still flagged `puppy` server-side —
 * i.e. the customer never confirmed the transition. Past this point we stop
 * actively nudging (intro modal + banner) and switch to a softer in-context
 * reminder inside the growth phase card.
 */
export function isPastAdultGracePeriod(
  pet: { dog_stage?: string; adult_when?: string } | null | undefined,
): boolean {
  if (!pet || pet.dog_stage !== 'puppy' || !pet.adult_when) return false;

  const adultDate = new Date(pet.adult_when);
  if (isNaN(adultDate.getTime())) return false;

  const graceEnd = new Date(adultDate);
  graceEnd.setMonth(graceEnd.getMonth() + ADULT_GRACE_PERIOD_MONTHS);
  return Date.now() >= graceEnd.getTime();
}

/**
 * Returns true if the pet is a puppy within 14 days of becoming adult
 * (or already past the adult date). Used by consumer components to decide
 * which modal mode to open.
 */
export function isNearAdultTransition(
  pet: { dog_stage?: string; adult_when?: string } | null | undefined,
): boolean {
  if (!pet || pet.dog_stage !== 'puppy' || !pet.adult_when) return false;

  const daysUntilAdult = (new Date(pet.adult_when).getTime() - Date.now()) / MS_PER_DAY;
  return daysUntilAdult <= DAYS_THRESHOLD;
}
